import React, { PureComponent } from 'react';
import { toastr } from 'react-redux-toastr';
import ModalContainer from '../../../../components/shared/modal/ModalContainer';
import { ModalFooterPrimaryButton } from '../../../../components/shared/modal/modalFooterPillButtons';
import LoadingBlueOrange from '../../../../components/shared/icons/LoadingBlueOrange';
import {
  getDocumentosColetaLaudo,
  uploadDocumentoColeta,
  getDocumentosColetaPdf,
  downloadDocumentoColeta,
} from '../../../../services/print';
import {
  ChecklistBadge,
  ChecklistIcon,
  ChecklistItem,
  ChecklistLabel,
  ChecklistPanel,
  ChecklistText,
  ChecklistTitle,
  ErrorText,
  HiddenFileInput,
  HistoricoItem,
  HistoricoList,
  HistoricoSection,
  HistoricoTitle,
  LoadingWrap,
  ModalBodyRow,
  ModalLayout,
  PageDot,
  PageDots,
  PreviewEmpty,
  PreviewFrame,
  PreviewHeader,
  PreviewImage,
  PreviewImageWrap,
  PreviewLoadingOverlay,
  PreviewContent,
  PreviewPanel,
  PreviewTitle,
  PreviewTitleRow,
  PreviewVersionBadge,
  PreviewToolbar,
  ToolbarButton,
} from './documentosColetaModal.styles';

const formatDateTime = (value) => {
  if (!value) return '';
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return value;
  return date.toLocaleString('pt-BR');
};

const sanitizeFilename = (value) =>
  String(value || 'documento')
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '')
    .replace(/[^a-zA-Z0-9-_]+/g, '-')
    .replace(/-+/g, '-')
    .replace(/^-|-$/g, '')
    .toLowerCase();

const extensionFromContentType = (contentType) => {
  if (!contentType) return 'jpg';
  if (contentType.includes('png')) return 'png';
  if (contentType.includes('gif')) return 'gif';
  if (contentType.includes('webp')) return 'webp';
  if (contentType.includes('pdf')) return 'pdf';
  return 'jpg';
};

const parseFilenameFromContentDisposition = (header) => {
  if (!header || typeof header !== 'string') return null;
  const utf8Match = header.match(/filename\*=UTF-8''([^;]+)/i);
  if (utf8Match && utf8Match[1]) {
    try {
      return decodeURIComponent(utf8Match[1].trim());
    } catch (err) {
      return utf8Match[1].trim();
    }
  }
  const match = header.match(/filename="?([^";]+)"?/i);
  return match && match[1] ? match[1].trim() : null;
};

const blobErrorMessage = async (blob, fallback) => {
  if (!blob || blob.type !== 'application/json') {
    return fallback;
  }
  try {
    const text = await blob.text();
    const json = JSON.parse(text);
    return json.message || fallback;
  } catch (err) {
    return fallback;
  }
};

const triggerBlobDownload = (blob, filename) => {
  const objectUrl = URL.createObjectURL(blob);
  const link = document.createElement('a');
  link.href = objectUrl;
  link.download = filename;
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
  URL.revokeObjectURL(objectUrl);
};

class DocumentosColetaModal extends PureComponent {
  state = {
    loading: false,
    uploading: false,
    error: null,
    payload: null,
    selectedPretendenteId: null,
    selectedTipo: null,
    selectedPage: 0,
    viewingHistoricoId: null,
    showHistorico: false,
    downloadingFile: false,
    previewImageLoading: false,
  };

  fileInputRef = React.createRef();

  componentDidUpdate(prevProps) {
    if (this.props.isOpen && !prevProps.isOpen) {
      this.loadDocumentos();
    }
  }

  getSecoes = () => {
    const { payload } = this.state;
    if (!payload || !Array.isArray(payload.secoes)) return [];
    return payload.secoes;
  };

  getSecao = () => {
    const { selectedPretendenteId } = this.state;
    const secoes = this.getSecoes();
    if (selectedPretendenteId) {
      return secoes.find(
        (secao) => Number(secao.pretendente_id) === Number(selectedPretendenteId),
      ) || null;
    }
    return secoes.length > 0 ? secoes[0] : null;
  };

  getDocumentos = () => {
    const secao = this.getSecao();
    return secao && Array.isArray(secao.documentos) ? secao.documentos : [];
  };

  getSelectedDocumento = () => {
    const documentos = this.getDocumentos();
    const { selectedTipo } = this.state;
    return documentos.find((doc) => doc.tipo === selectedTipo) || documentos[0] || null;
  };

  getActivePaginas = () => {
    const documento = this.getSelectedDocumento();
    if (!documento) return [];

    const { viewingHistoricoId } = this.state;
    if (viewingHistoricoId) {
      const historico = (documento.historico || []).find(
        (item) => Number(item.id) === Number(viewingHistoricoId),
      );
      return historico && Array.isArray(historico.paginas) ? historico.paginas : [];
    }

    return Array.isArray(documento.paginas) ? documento.paginas : [];
  };

  getPreviewImageKey = () => {
    const { viewingHistoricoId, selectedPage, selectedTipo } = this.state;
    return `${viewingHistoricoId || 'atual'}-${selectedTipo || 'tipo'}-${selectedPage}`;
  };

  preloadPaginas = (paginas) => {
    if (!Array.isArray(paginas)) return;
    paginas.forEach((pagina) => {
      if (pagina && pagina.url) {
        const img = new Image();
        img.src = pagina.url;
      }
    });
  };

  preloadHistoricoImages = () => {
    const documento = this.getSelectedDocumento();
    if (!documento) return;

    this.preloadPaginas(documento.paginas);
    (documento.historico || []).forEach((item) => {
      this.preloadPaginas(item.paginas);
    });
  };

  handlePreviewImageLoad = () => {
    this.setState({ previewImageLoading: false });
  };

  handlePreviewImageRef = (element) => {
    if (element && element.complete && this.state.previewImageLoading) {
      window.requestAnimationFrame(() => {
        if (this.state.previewImageLoading) {
          this.handlePreviewImageLoad();
        }
      });
    }
  };

  handlePreviewImageError = () => {
    this.setState({ previewImageLoading: false });
  };

  selectPage = (index) => {
    this.setState({ selectedPage: index, previewImageLoading: true });
  };

  toggleHistorico = () => {
    const showHistorico = !this.state.showHistorico;
    this.setState({ showHistorico }, () => {
      if (showHistorico) {
        this.preloadHistoricoImages();
      }
    });
  };

  loadDocumentos = async (options = {}) => {
    const { keepSelection = false, tipoToKeep = null } = options;
    const { token, solicitacaoId, pretendenteId: propPretendenteId } = this.props;
    const pretendenteIdToKeep = keepSelection
      ? (this.state.selectedPretendenteId || propPretendenteId)
      : null;

    this.setState({
      loading: true,
      error: null,
      payload: keepSelection ? this.state.payload : null,
      selectedTipo: keepSelection ? (tipoToKeep || this.state.selectedTipo) : null,
      selectedPage: 0,
      viewingHistoricoId: null,
      showHistorico: false,
    });

    try {
      const params = {};
      const effectivePretendenteId = propPretendenteId || pretendenteIdToKeep;
      if (effectivePretendenteId) {
        params.pretendente_id = effectivePretendenteId;
      }
      const result = await getDocumentosColetaLaudo(token, solicitacaoId, params);
      if (result.status === 401) {
        this.props.onUnauthorized && this.props.onUnauthorized();
        return;
      }
      if (result.status >= 400) {
        this.setState({
          loading: false,
          error: (result.data && result.data.message) || 'Não foi possível carregar os documentos.',
        });
        return;
      }

      const secoes = result.data && Array.isArray(result.data.secoes) ? result.data.secoes : [];
      const initialPretendenteId = propPretendenteId
        || pretendenteIdToKeep
        || (secoes[0] && secoes[0].pretendente_id);
      const secaoInicial = secoes.find(
        (secao) => Number(secao.pretendente_id) === Number(initialPretendenteId),
      ) || secoes[0];
      const preferredTipo = keepSelection ? (tipoToKeep || this.state.selectedTipo) : null;
      const firstTipo = preferredTipo
        || (secaoInicial && Array.isArray(secaoInicial.documentos) && secaoInicial.documentos.length > 0
          ? secaoInicial.documentos[0].tipo
          : null);

      this.setState({
        loading: false,
        payload: result.data,
        selectedPretendenteId: initialPretendenteId || null,
        selectedTipo: firstTipo,
        selectedPage: 0,
        viewingHistoricoId: null,
        previewImageLoading: Boolean(firstTipo),
      });
    } catch (err) {
      this.setState({
        loading: false,
        error: err.message || 'Erro ao carregar os documentos.',
      });
    }
  };

  selectPretendente = (pretendenteId) => {
    const secao = this.getSecoes().find(
      (item) => Number(item.pretendente_id) === Number(pretendenteId),
    );
    const firstTipo = secao && Array.isArray(secao.documentos) && secao.documentos.length > 0
      ? secao.documentos[0].tipo
      : null;

    this.setState({
      selectedPretendenteId: pretendenteId,
      selectedTipo: firstTipo,
      selectedPage: 0,
      viewingHistoricoId: null,
      showHistorico: false,
      previewImageLoading: Boolean(firstTipo),
    });
  };

  selectTipo = (tipo) => {
    this.setState({
      selectedTipo: tipo,
      selectedPage: 0,
      viewingHistoricoId: null,
      showHistorico: false,
      previewImageLoading: true,
    });
  };

  selectHistorico = (historicoId) => {
    this.setState({
      viewingHistoricoId: historicoId,
      selectedPage: 0,
      showHistorico: true,
      previewImageLoading: true,
    });
  };

  viewAtual = () => {
    this.setState({
      viewingHistoricoId: null,
      selectedPage: 0,
      previewImageLoading: true,
    });
  };

  handleUploadClick = () => {
    if (this.fileInputRef.current) {
      this.fileInputRef.current.click();
    }
  };

  handleFileChange = async (event) => {
    const file = event.target.files && event.target.files[0];
    event.target.value = '';
    if (!file) return;

    const documento = this.getSelectedDocumento();
    const { token, solicitacaoId } = this.props;
    const pretendenteId = this.state.selectedPretendenteId || this.props.pretendenteId;
    if (!documento || !pretendenteId) {
      toastr.error('Selecione um pretendente para enviar o documento.');
      return;
    }

    this.setState({ uploading: true });
    try {
      const formData = new FormData();
      formData.append('pretendente_id', String(pretendenteId));
      formData.append('tipo', documento.tipo);
      formData.append('arquivo', file);

      const result = await uploadDocumentoColeta(token, solicitacaoId, formData);
      if (result.status === 401) {
        this.setState({ uploading: false });
        this.props.onUnauthorized && this.props.onUnauthorized();
        return;
      }
      if (result.status >= 400) {
        toastr.error((result.data && result.data.message) || 'Não foi possível enviar o arquivo.');
        this.setState({ uploading: false });
        return;
      }

      toastr.success('Documento atualizado com sucesso.');
      await this.loadDocumentos({ keepSelection: true, tipoToKeep: documento.tipo });
      this.setState({ uploading: false });
    } catch (err) {
      toastr.error(err.message || 'Erro ao enviar o arquivo.');
      this.setState({ uploading: false });
    }
  };

  handleDownloadCurrentFile = async () => {
    const documento = this.getSelectedDocumento();
    const { token, solicitacaoId } = this.props;
    const pretendenteId = this.state.selectedPretendenteId || this.props.pretendenteId;
    const { viewingHistoricoId } = this.state;

    if (!documento || !pretendenteId) {
      toastr.error('Selecione um pretendente para baixar o documento.');
      return;
    }

    const paginas = this.getActivePaginas();
    if (paginas.length === 0) {
      toastr.error('Nenhum arquivo disponível para download.');
      return;
    }

    const params = {
      pretendente_id: pretendenteId,
      tipo: documento.tipo,
    };
    if (viewingHistoricoId) {
      params.historico_id = viewingHistoricoId;
    }

    this.setState({ downloadingFile: true });
    try {
      const result = await downloadDocumentoColeta(token, solicitacaoId, params);
      if (result.status === 401) {
        this.props.onUnauthorized && this.props.onUnauthorized();
        return;
      }
      if (result.status >= 400) {
        const message = await blobErrorMessage(
          result.data,
          'Não foi possível baixar o arquivo.',
        );
        toastr.error(message);
        return;
      }

      const headerFilename = parseFilenameFromContentDisposition(
        result.headers && result.headers['content-disposition'],
      );
      const contentType = result.data.type || 'application/octet-stream';
      const ext = extensionFromContentType(contentType);
      const baseName = sanitizeFilename(documento.tipo_label);
      const suffix = viewingHistoricoId ? '-versao-anterior' : '';
      const fallbackFilename = `${baseName}${suffix}.${ext}`;
      const filename = headerFilename || fallbackFilename;

      triggerBlobDownload(result.data, filename);
    } catch (err) {
      toastr.error(err.message || 'Erro ao baixar o arquivo.');
    } finally {
      this.setState({ downloadingFile: false });
    }
  };

  handleDownloadPdf = async () => {
    const { token, solicitacaoId, pretendenteId: propPretendenteId } = this.props;
    // Só filtra por pretendente quando o modal foi aberto pelo card; "Ver Documentos" gera PDF de todos.
    try {
      toastr.info('Aguarde, estamos gerando o PDF dos documentos.', {
        timeOut: 0,
        extendedTimeOut: 0,
      });
      const params = {};
      if (propPretendenteId) {
        params.pretendente_id = propPretendenteId;
      }
      const result = await getDocumentosColetaPdf(token, solicitacaoId, params);
      toastr.removeByType('info');
      if (result.status >= 400) {
        toastr.error((result.data && result.data.message) || 'Não foi possível gerar o PDF.');
        return;
      }
      window.open(result.data, '_blank');
    } catch (err) {
      toastr.removeByType('info');
      toastr.error(err.message || 'Erro ao gerar PDF.');
    }
  };

  renderChecklist = () => {
    const documentos = this.getDocumentos();
    const { selectedTipo } = this.state;

    return documentos.map((documento) => {
      const done = Array.isArray(documento.paginas) && documento.paginas.length > 0;
      const pending = documento.pendente || documento.indisponivel || !done;

      return (
        <ChecklistItem
          key={documento.tipo}
          type="button"
          active={selectedTipo === documento.tipo}
          onClick={() => this.selectTipo(documento.tipo)}
        >
          <ChecklistIcon done={done}>{done ? '✓' : '○'}</ChecklistIcon>
          <ChecklistText>
            <ChecklistLabel>{documento.tipo_label}</ChecklistLabel>
            {pending && <ChecklistBadge pending>PENDENTE</ChecklistBadge>}
          </ChecklistText>
        </ChecklistItem>
      );
    });
  };

  renderPreview = () => {
    const documento = this.getSelectedDocumento();
    const paginas = this.getActivePaginas();
    const { selectedPage, previewImageLoading } = this.state;

    if (!documento) {
      return <PreviewEmpty>Nenhum documento disponível.</PreviewEmpty>;
    }

    if (paginas.length === 0) {
      return (
        <PreviewEmpty>
          {documento.pendente
            ? 'Documento pendente. Envie um arquivo para este tipo.'
            : 'Documento indisponível para visualização.'}
        </PreviewEmpty>
      );
    }

    const pagina = paginas[selectedPage] || paginas[0];
    const imageKey = this.getPreviewImageKey();

    return (
      <PreviewContent>
        <PreviewImageWrap>
          {previewImageLoading && (
            <PreviewLoadingOverlay>
              <LoadingBlueOrange width="22px" />
              <span>Carregando visualização…</span>
            </PreviewLoadingOverlay>
          )}
          <PreviewImage
            key={imageKey}
            ref={this.handlePreviewImageRef}
            src={pagina.url}
            alt={documento.tipo_label}
            $isLoading={previewImageLoading}
            onLoad={this.handlePreviewImageLoad}
            onError={this.handlePreviewImageError}
          />
        </PreviewImageWrap>
        {paginas.length > 1 && (
          <PageDots>
            {paginas.map((_, index) => (
              <PageDot
                key={`page-dot-${index}`}
                type="button"
                active={index === selectedPage}
                onClick={() => this.selectPage(index)}
              />
            ))}
          </PageDots>
        )}
      </PreviewContent>
    );
  };

  renderHistorico = () => {
    const documento = this.getSelectedDocumento();
    if (!documento || !Array.isArray(documento.historico) || documento.historico.length === 0) {
      return null;
    }

    const { showHistorico, viewingHistoricoId } = this.state;

    return (
      <HistoricoSection>
        <HistoricoTitle type="button" onClick={this.toggleHistorico}>
          {showHistorico ? '▾' : '▸'} Histórico ({documento.historico.length})
        </HistoricoTitle>
        {showHistorico && (
          <HistoricoList>
            <HistoricoItem
              type="button"
              active={!viewingHistoricoId}
              onClick={this.viewAtual}
            >
              Versão atual
            </HistoricoItem>
            {documento.historico.map((item) => (
              <HistoricoItem
                key={item.id}
                type="button"
                active={Number(viewingHistoricoId) === Number(item.id)}
                onClick={() => this.selectHistorico(item.id)}
              >
                Substituído em {formatDateTime(item.substituido_em)}
                {item.origem ? ` · ${item.origem}` : ''}
              </HistoricoItem>
            ))}
          </HistoricoList>
        )}
      </HistoricoSection>
    );
  };

  renderBody = () => {
    const { loading, error, uploading, selectedPretendenteId, downloadingFile, viewingHistoricoId } = this.state;
    const { pretendenteNome, solicitacaoId } = this.props;
    const secao = this.getSecao();
    const displayNome = secao && secao.pretendente_nome ? secao.pretendente_nome : pretendenteNome;
    const secoes = this.getSecoes();
    const documento = this.getSelectedDocumento();
    const paginas = this.getActivePaginas();
    const currentUrl = paginas.length > 0 ? paginas[this.state.selectedPage || 0].url : null;

    if (loading) {
      return (
        <LoadingWrap>
          <LoadingBlueOrange width="24px" />
          <span style={{ marginLeft: 8 }}>Carregando documentos…</span>
        </LoadingWrap>
      );
    }

    if (error) {
      return <ErrorText>{error}</ErrorText>;
    }

    return (
      <ModalLayout>
        <ModalBodyRow>
          <ChecklistPanel>
            <ChecklistTitle>
              PROCESSO #{solicitacaoId}
              <br />
              Checklist de Documentos
            </ChecklistTitle>
            {secoes.length > 1 && (
              <div style={{ marginBottom: 12 }}>
                <select
                  value={selectedPretendenteId || ''}
                  onChange={(e) => this.selectPretendente(Number(e.target.value))}
                  style={{ width: '100%', padding: '8px 10px', borderRadius: 8, border: '1px solid #cbd5e1' }}
                >
                  {secoes.map((item) => (
                    <option key={item.pretendente_id} value={item.pretendente_id}>
                      {item.pretendente_nome}
                    </option>
                  ))}
                </select>
              </div>
            )}
            {this.renderChecklist()}
          </ChecklistPanel>
          <PreviewPanel>
            <PreviewHeader>
              <PreviewTitleRow>
                <PreviewTitle>
                  Visualização do Arquivo
                  {displayNome ? ` — ${displayNome}` : ''}
                  {documento ? ` · ${documento.tipo_label}` : ''}
                </PreviewTitle>
                {viewingHistoricoId && (
                  <PreviewVersionBadge>Versão anterior</PreviewVersionBadge>
                )}
              </PreviewTitleRow>
              <PreviewToolbar>
                <ToolbarButton
                  type="button"
                  disabled={!currentUrl || downloadingFile}
                  onClick={this.handleDownloadCurrentFile}
                >
                  {downloadingFile ? 'Baixando…' : 'Baixar arquivo'}
                </ToolbarButton>
              </PreviewToolbar>
            </PreviewHeader>
            <PreviewFrame>{this.renderPreview()}</PreviewFrame>
            {this.renderHistorico()}
            <HiddenFileInput
              ref={this.fileInputRef}
              type="file"
              accept="image/jpeg,image/png,image/webp,image/gif,application/pdf"
              onChange={this.handleFileChange}
            />
          </PreviewPanel>
        </ModalBodyRow>
        {uploading && (
          <LoadingWrap style={{ padding: '12px' }}>
            <LoadingBlueOrange width="18px" />
            <span style={{ marginLeft: 8 }}>Enviando arquivo…</span>
          </LoadingWrap>
        )}
      </ModalLayout>
    );
  };

  render() {
    const { isOpen, onClose, pretendenteNome } = this.props;
    const documento = this.getSelectedDocumento();
    const secao = this.getSecao();
    const displayNome = secao && secao.pretendente_nome ? secao.pretendente_nome : pretendenteNome;
    const activePretendenteId = this.state.selectedPretendenteId || this.props.pretendenteId;
    const canUpload = Boolean(activePretendenteId) && Boolean(documento);

    return (
      <ModalContainer
        isOpen={isOpen}
        onClose={onClose}
        size="lg"
        customstyle=".modal-dialog { max-width: 1120px; width: 96%; } .modal-body { padding: 0 16px 8px; }"
        header={
          <div style={{ textAlign: 'left', padding: '8px 0' }}>
            <div style={{ fontSize: 13, color: '#64748b' }}>Revisão de Documentos</div>
            <div style={{ fontSize: 18, fontWeight: 700, color: '#152b4e' }}>
              {displayNome || 'LucIA COLETA'}
            </div>
          </div>
        }
        footer={
          <React.Fragment>
            <ModalFooterPrimaryButton onClick={onClose}>Fechar</ModalFooterPrimaryButton>
            <ModalFooterPrimaryButton onClick={this.handleDownloadPdf}>
              Baixar PDF
            </ModalFooterPrimaryButton>
            <ModalFooterPrimaryButton
              onClick={this.handleUploadClick}
              disabled={!canUpload || this.state.uploading}
            >
              {documento && (documento.pendente || documento.indisponivel)
                ? 'Enviar arquivo'
                : 'Substituir arquivo'}
            </ModalFooterPrimaryButton>
          </React.Fragment>
        }
      >
        {this.renderBody()}
      </ModalContainer>
    );
  }
}

export default DocumentosColetaModal;
